import type Phaser from 'phaser';
import { MOD_TEXT } from '../../content/mods';
import type { MemoryLog } from '../../content/story';
import { LANE_COUNT } from '../../sim/constants';
import type { ModId } from '../../sim/mods';
import { powerState, type PowerState, type Simulation } from '../../sim/simulation';
import { TUNING } from '../../sim/tuning';
import { RADIATION, hexNumber } from '../render/palette';
import { CELL_H, CELL_W, PIXEL_FONT } from './pixelFont';
import { wrap } from './wrap';

const DEPTH = 900;
const RAD_COLOR = hexNumber(RADIATION.hot);
const RAD_BAR_W = 36;
const BAR_W = 72;
const BAR_H = 5;
const BOSS_BAR_W = 140;
const LOG_SECONDS = 9;
/** Where tips sit: under the top corners' readouts. */
const TIP_Y = 42;

/** What the HUD shows, read from whichever simulation is running (a room or a transit). */
export interface HudState {
  readonly armor: number;
  readonly armorMax: number;
  readonly starlight: number;
  readonly starlightMax: number;
  /** Rare materials in the save. */
  readonly materials: number;
  /** The player's depth lane, or null where there are no lanes (transit). */
  readonly lane: number | null;
  readonly rewinds: number;
  /** A boss's health while one stands. */
  readonly boss: { readonly hp: number; readonly maxHp: number; readonly hitFlash: number } | null;
  /** Flight progress 0–1 (transit only): a bar along the bottom to the destination. */
  readonly progress?: number;
  /** Weapon mods held, in the order found (rooms only). */
  readonly mods?: readonly ModId[];
  /** The radiation dose toward the next burned plate, 0–1, and whether it is rising now. */
  readonly radiation?: { readonly dose: number; readonly exposed: boolean };
  /** Shown with ?debug. */
  readonly x: number;
}

/** The HUD's view of a room. */
export function roomHudState(sim: Simulation, materials: number): HudState {
  const p = sim.player;
  const boss = sim.enemies.find((e) => e.kind === 'ranger' && e.alive) ?? null;
  return {
    armor: p.armor,
    armorMax: sim.suit.armorMax,
    starlight: p.starlight,
    starlightMax: sim.suit.starlightMax,
    materials,
    lane: p.lane,
    rewinds: sim.rewinds,
    boss,
    mods: p.mods,
    radiation: { dose: p.dose / TUNING.radiationTicks, exposed: p.exposed },
    x: p.x,
  };
}

const STATE_COLOR: Record<PowerState, number> = {
  charged: 0xffd23f,
  low: 0xff8a24,
  flutter: 0xff5a8a,
  empty: 0x6a6f85,
};

const STATE_NAME: Record<PowerState, string> = {
  charged: 'CHARGED',
  low: 'LOW',
  flutter: 'FLUTTER',
  empty: 'EMPTY',
};

/**
 * Top-left: armor plates, the Starlight bar with marks at the power-state thresholds, lane pips
 * (with the radiation dose beside them while it lasts), and the weapon mods held. Top-right: the room, rewinds this session (and the seed and frame rate with ?debug).
 * Top-centre: a boss's name and health while one is in the room, or a tip (the tutorial's, or the suit's first-time words). Memory
 * logs show in a panel. In a transit, a bar along the bottom shows the way to the destination.
 */
export class Hud {
  private readonly gfx: Phaser.GameObjects.Graphics;
  private readonly stateText: Phaser.GameObjects.BitmapText;
  private readonly radText: Phaser.GameObjects.BitmapText;
  private readonly modText: Phaser.GameObjects.BitmapText;
  private readonly rightText: Phaser.GameObjects.BitmapText;
  private readonly banner: Phaser.GameObjects.BitmapText;
  private readonly bossName: Phaser.GameObjects.BitmapText;
  private readonly logPanel: Phaser.GameObjects.Graphics;
  private readonly logText: Phaser.GameObjects.BitmapText;
  private readonly tipPanel: Phaser.GameObjects.Graphics;
  private readonly tipText: Phaser.GameObjects.BitmapText;
  private tip: string | null = null;
  /** The destination's colour at the end of the transit's progress bar (set by the transit). */
  private destination = 0xffffff;
  private readonly debug = new URLSearchParams(window.location.search).has('debug');
  private roomLabel = '';
  private seed = '';

  constructor(private readonly scene: Phaser.Scene) {
    this.gfx = scene.add.graphics().setDepth(DEPTH);
    this.stateText = scene.add
      .bitmapText(10 + BAR_W + 6, 17, PIXEL_FONT, '', CELL_W)
      .setDepth(DEPTH);
    this.radText = scene.add
      .bitmapText(36 + RAD_BAR_W + 5, 26, PIXEL_FONT, 'RADIATION', CELL_W)
      .setTint(RAD_COLOR)
      .setDepth(DEPTH)
      .setVisible(false);
    this.modText = scene.add
      .bitmapText(10, 36, PIXEL_FONT, '', CELL_W)
      .setTint(0x7df9ff)
      .setDepth(DEPTH);
    this.rightText = scene.add
      .bitmapText(0, 6, PIXEL_FONT, '', CELL_W)
      .setOrigin(1, 0)
      .setRightAlign()
      .setDepth(DEPTH);
    this.banner = scene.add
      .bitmapText(0, 0, PIXEL_FONT, '', CELL_W * 2)
      .setOrigin(0.5)
      .setTint(0xffd23f)
      .setDepth(DEPTH + 1)
      .setAlpha(0);
    this.bossName = scene.add
      .bitmapText(0, 5, PIXEL_FONT, 'RANGER', CELL_W)
      .setOrigin(0.5, 0)
      .setTint(0xff6a5a)
      .setDepth(DEPTH)
      .setVisible(false);
    this.logPanel = scene.add
      .graphics()
      .setDepth(DEPTH + 2)
      .setAlpha(0);
    this.logText = scene.add
      .bitmapText(0, 0, PIXEL_FONT, '', CELL_W)
      .setOrigin(0.5, 0)
      .setCenterAlign()
      .setDepth(DEPTH + 3)
      .setAlpha(0);
    this.tipPanel = scene.add.graphics().setDepth(DEPTH);
    this.tipText = scene.add
      .bitmapText(0, TIP_Y, PIXEL_FONT, '', CELL_W)
      .setOrigin(0.5, 0)
      .setCenterAlign()
      .setTint(0x9fd8ff)
      .setDepth(DEPTH + 1);
  }

  /** A tip in a panel at the top (null hides it). A new tip fades in. */
  setTip(text: string | null): void {
    if (text === this.tip) return;
    this.tip = text;
    this.layoutTip();
    for (const part of [this.tipPanel, this.tipText]) {
      this.scene.tweens.killTweensOf(part);
      part.setAlpha(0);
      this.scene.tweens.add({ targets: part, alpha: 1, duration: 250 });
    }
  }

  /** Lays out what depends on the screen's width again (after a rotation or a resize). */
  resize(): void {
    this.layoutTip();
  }

  /** Wraps the tip to the screen's width and draws its panel, centred. */
  private layoutTip(): void {
    const text = this.tip;
    this.tipPanel.clear();
    this.tipText.setText('');
    if (text === null) return;
    const w = this.scene.scale.width;
    const lines = wrap(text, Math.floor((w - 200) / CELL_W));
    const panelW = Math.max(...lines.map((l) => l.length)) * CELL_W + 16;
    const panelH = lines.length * CELL_H + 8;
    const x = Math.round((w - panelW) / 2);
    this.tipText.setText(lines.join('\n')).setX(Math.round(w / 2));
    this.tipPanel
      .fillStyle(0x0b0820, 0.8)
      .fillRect(x, TIP_Y - 5, panelW, panelH)
      .lineStyle(1, 0x46557e, 1)
      .strokeRect(x + 0.5, TIP_Y - 4.5, panelW - 1, panelH - 1);
  }

  /** The colour of the planet the transit is heading for. */
  setDestination(color: number): void {
    this.destination = color;
  }

  /** A memory log in a panel across the lower screen; fades after a few seconds. */
  showLog(log: MemoryLog): void {
    const w = this.scene.scale.width;
    const h = this.scene.scale.height;
    this.logText
      .setText([log.title, '', ...log.lines].join('\n'))
      .setPosition(Math.round(w / 2), h - 92);
    const panelW = Math.max(log.title.length, ...log.lines.map((l) => l.length)) * CELL_W + 24;
    const x = Math.round((w - panelW) / 2);
    this.logPanel
      .clear()
      .fillStyle(0x0b0820, 0.88)
      .fillRect(x, h - 100, panelW, 82)
      .lineStyle(1, 0xff6a5a, 0.9)
      .strokeRect(x + 0.5, h - 99.5, panelW - 1, 81);
    for (const part of [this.logPanel, this.logText]) {
      this.scene.tweens.killTweensOf(part);
      part.setAlpha(1);
      this.scene.tweens.add({ targets: part, alpha: 0, delay: LOG_SECONDS * 1000, duration: 800 });
    }
  }

  /** Where the player is in the run, shown top-right (the seed only with ?debug). */
  setRoom(label: string, seed: string): void {
    this.roomLabel = label;
    this.seed = seed;
  }

  /** A big word in the middle of the screen that fades out (REWIND). */
  flash(text: string): void {
    this.banner
      .setText(text)
      .setPosition(
        Math.round(this.scene.scale.width / 2),
        Math.round(this.scene.scale.height / 2 - 30),
      );
    this.banner.setAlpha(1);
    this.scene.tweens.add({ targets: this.banner, alpha: 0, delay: 500, duration: 700 });
  }

  update(p: HudState, fps: number): void {
    const g = this.gfx;
    g.clear();

    // Armor plates.
    for (let i = 0; i < p.armorMax; i++) {
      const x = 10 + i * 10;
      g.fillStyle(0x0b0820, 0.7).fillRect(x - 1, 5, 9, 9);
      g.fillStyle(i < p.armor ? 0x9fe0ff : 0x2a2f45, 1).fillRect(x, 6, 7, 7);
      if (i < p.armor) g.fillStyle(0xffffff, 1).fillRect(x + 1, 7, 2, 2);
    }

    // Starlight bar, coloured by power state, with the Low and Charged thresholds marked.
    const state = powerState(p.starlight);
    const fill = Math.round((p.starlight / p.starlightMax) * BAR_W);
    g.fillStyle(0x0b0820, 0.8).fillRect(9, 17, BAR_W + 2, BAR_H + 2);
    g.fillStyle(STATE_COLOR[state], 1).fillRect(10, 18, fill, BAR_H);
    g.fillStyle(0xffffff, 0.8);
    for (const mark of [TUNING.lowAt, TUNING.chargedAt]) {
      g.fillRect(10 + Math.round((mark / p.starlightMax) * BAR_W), 17, 1, BAR_H + 2);
    }
    this.stateText.setText(STATE_NAME[state]).setTint(STATE_COLOR[state]);

    // Lane pips: the current lane is lit; the far lane is the rightmost pip.
    if (p.lane !== null) {
      for (let i = 0; i < LANE_COUNT; i++) {
        const lit = i === p.lane;
        g.fillStyle(lit ? 0xffffff : 0x4a4f68, 1).fillRect(
          10 + i * 7,
          27,
          lit ? 5 : 4,
          lit ? 5 : 4,
        );
      }
    }

    this.drawRadiation(p.radiation);
    // One a line: a narrow column, clear of the tips centred at the top.
    this.modText.setText((p.mods ?? []).map((m) => MOD_TEXT[m].short).join('\n'));

    this.drawBossBar(p.boss);
    if (p.progress !== undefined) this.drawProgress(p.progress);

    const lines = [this.roomLabel, `MATERIALS ${p.materials}`, `REWINDS ${p.rewinds}`].filter(
      Boolean,
    );
    if (this.debug)
      lines.push(`SEED ${this.seed}`, `${Math.round(fps)} FPS`, `X ${Math.round(p.x)}`);
    // Left of the pause chip.
    this.rightText.setText(lines.join('\n')).setX(this.scene.scale.width - 26);
  }

  /** The way to the destination: a dotted line, the ship's mark on it, the planet at the end. */
  private drawProgress(progress: number): void {
    const g = this.gfx;
    const w = this.scene.scale.width;
    const y = this.scene.scale.height - 9;
    const x0 = 40;
    const x1 = w - 40;
    const at = Math.round(x0 + (x1 - x0) * progress);
    g.fillStyle(0x4a4f68, 1);
    for (let x = at + 4; x < x1 - 4; x += 4) g.fillRect(x, y, 2, 1);
    g.fillStyle(0x9fd8ff, 1).fillRect(x0, y, at - x0, 1);
    g.fillStyle(0xffffff, 1).fillRect(at - 1, y - 2, 3, 5);
    g.fillStyle(this.destination, 1).fillCircle(x1, y, 4);
    g.fillStyle(0x7df9ff, 1).fillRect(x1 - 1, y - 5, 2, 1);
  }

  /** The dose toward the next burned plate; the word blinks while it is rising. */
  private drawRadiation(rad: HudState['radiation']): void {
    const shown = !!rad && (rad.exposed || rad.dose > 0);
    this.radText.setVisible(
      shown && (!rad.exposed || Math.floor(this.scene.time.now / 250) % 2 === 0),
    );
    if (!shown) return;
    const g = this.gfx;
    g.fillStyle(0x0b0820, 0.8).fillRect(35, 27, RAD_BAR_W + 2, BAR_H);
    g.fillStyle(RAD_COLOR, rad.exposed ? 1 : 0.5).fillRect(
      36,
      28,
      Math.round(rad.dose * RAD_BAR_W),
      BAR_H - 2,
    );
  }

  /** The boss's health, with marks where it changes phase (thirds). */
  private drawBossBar(boss: HudState['boss']): void {
    this.bossName.setVisible(!!boss);
    if (!boss) return;
    const g = this.gfx;
    const x = Math.round((this.scene.scale.width - BOSS_BAR_W) / 2);
    this.bossName.setX(Math.round(this.scene.scale.width / 2));
    const fill = Math.round((boss.hp / boss.maxHp) * BOSS_BAR_W);
    g.fillStyle(0x0b0820, 0.8).fillRect(x - 1, 16, BOSS_BAR_W + 2, BAR_H + 2);
    g.fillStyle(boss.hitFlash > 0 ? 0xffffff : 0xff4a3a, 1).fillRect(x, 17, fill, BAR_H);
    g.fillStyle(0xffffff, 0.7);
    for (const mark of [1 / 3, 2 / 3]) {
      g.fillRect(x + Math.round(mark * BOSS_BAR_W), 16, 1, BAR_H + 2);
    }
  }
}
